import * as dagre from "dagre"

import type {
  HostedAgentLifecycleRequest,
  HostedAgentLink,
  HostedAgentListItem,
} from "@/api/admin-api"
import type { Edge, Node, XYPosition } from "@xyflow/react"

// =============================================================================
// Agent map (B3) — pure helpers shared by the canvas, node and edge components.
// The map is a VIEW over `agents[]` + the tenant link register; nothing here
// is persisted server-side except through the link mutations. Node positions
// are a per-browser convenience (localStorage) — never part of the data model.
// =============================================================================

export const HOSTED_AGENT_MAP_NODE_TYPE = "hostedAgent" as const
export const HOSTED_AGENT_MAP_EDGE_TYPE = "agentLink" as const

/** Layout box used before the node has been measured. */
export const HOSTED_AGENT_MAP_NODE_WIDTH = 260
export const HOSTED_AGENT_MAP_NODE_HEIGHT = 132

const NODE_SPACING = 48
const RANK_SPACING = 140
const GRID_COLUMNS = 3

/** Longest mission (`profile.description`) excerpt shown on a card. */
export const HOSTED_AGENT_MAP_MISSION_MAX = 110

const POSITIONS_STORAGE_KEY = "admin-hosted-agent-map-positions"

export type HostedAgentMapNodeData = {
  agent: HostedAgentListItem
  /** Active lifecycle request for this agent (tenant-wide poll), if any. */
  lifecycle: HostedAgentLifecycleRequest | null
  [key: string]: unknown
}

export type HostedAgentLinkEdgeData = {
  link: HostedAgentLink
  [key: string]: unknown
}

export type HostedAgentMapNode = Node<HostedAgentMapNodeData, typeof HOSTED_AGENT_MAP_NODE_TYPE>
export type HostedAgentLinkEdge = Edge<HostedAgentLinkEdgeData, typeof HOSTED_AGENT_MAP_EDGE_TYPE>

export type HostedAgentMapPositions = Record<string, XYPosition>

export function truncateHostedAgentMission(text: string, max = HOSTED_AGENT_MAP_MISSION_MAX) {
  const normalized = text.replace(/\s+/g, " ").trim()
  if (normalized.length <= max) return normalized
  return `${normalized.slice(0, max - 1).trimEnd()}…`
}

// -----------------------------------------------------------------------------
// localStorage positions (per browser; wrapped so private mode never throws)
// -----------------------------------------------------------------------------

export function loadHostedAgentMapPositions(): HostedAgentMapPositions {
  try {
    const raw = window.localStorage.getItem(POSITIONS_STORAGE_KEY)
    if (!raw) return {}
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) return {}
    const positions: HostedAgentMapPositions = {}
    for (const [id, value] of Object.entries(parsed as Record<string, unknown>)) {
      if (
        typeof value === "object" &&
        value !== null &&
        typeof (value as XYPosition).x === "number" &&
        typeof (value as XYPosition).y === "number"
      ) {
        positions[id] = { x: (value as XYPosition).x, y: (value as XYPosition).y }
      }
    }
    return positions
  } catch {
    return {}
  }
}

export function saveHostedAgentMapPositions(positions: HostedAgentMapPositions): void {
  try {
    window.localStorage.setItem(POSITIONS_STORAGE_KEY, JSON.stringify(positions))
  } catch {
    // Storage unavailable (private mode / quota) — positions are a convenience only.
  }
}

export function clearHostedAgentMapPositions(): void {
  try {
    window.localStorage.removeItem(POSITIONS_STORAGE_KEY)
  } catch {
    // ignore
  }
}

// -----------------------------------------------------------------------------
// Graph construction
// -----------------------------------------------------------------------------

export function hostedAgentsToNodes(
  agents: HostedAgentListItem[],
  positions: HostedAgentMapPositions,
  lifecycleByAgentId: Map<string, HostedAgentLifecycleRequest>
): HostedAgentMapNode[] {
  return agents.map((agent) => ({
    id: agent.id,
    type: HOSTED_AGENT_MAP_NODE_TYPE,
    position: positions[agent.id] ?? { x: 0, y: 0 },
    data: { agent, lifecycle: lifecycleByAgentId.get(agent.id) ?? null },
  }))
}

/**
 * Links → edges. Links whose endpoints are not in `agents` (a target deleted
 * in another tab, a stale cache) are dropped so react-flow never warns about
 * dangling edges; the backend cleans them up on agent delete.
 */
export function hostedAgentLinksToEdges(
  links: HostedAgentLink[],
  agents: HostedAgentListItem[]
): HostedAgentLinkEdge[] {
  const known = new Set(agents.map((agent) => agent.id))
  return links
    .filter((link) => known.has(link.from_agent_id) && known.has(link.to_agent_id))
    .map((link) => ({
      id: link.id,
      type: HOSTED_AGENT_MAP_EDGE_TYPE,
      source: link.from_agent_id,
      target: link.to_agent_id,
      data: { link },
    }))
}

export function findHostedAgentLink(
  links: HostedAgentLink[],
  fromAgentId: string,
  toAgentId: string
): HostedAgentLink | undefined {
  return links.find((link) => link.from_agent_id === fromAgentId && link.to_agent_id === toAgentId)
}

/**
 * Auto-layout: dagre left→right when the graph has edges (arrows read
 * naturally from caller to callee), a simple grid otherwise — dagre would
 * stack every isolated node into one column.
 */
export function layoutHostedAgentMap(
  nodes: HostedAgentMapNode[],
  edges: HostedAgentLinkEdge[]
): HostedAgentMapPositions {
  const positions: HostedAgentMapPositions = {}
  if (nodes.length === 0) return positions

  if (edges.length === 0) {
    nodes.forEach((node, index) => {
      positions[node.id] = {
        x: (index % GRID_COLUMNS) * (HOSTED_AGENT_MAP_NODE_WIDTH + NODE_SPACING),
        y: Math.floor(index / GRID_COLUMNS) * (HOSTED_AGENT_MAP_NODE_HEIGHT + NODE_SPACING),
      }
    })
    return positions
  }

  const graph = new dagre.graphlib.Graph()
  graph.setDefaultEdgeLabel(() => ({}))
  graph.setGraph({ rankdir: "LR", nodesep: NODE_SPACING, ranksep: RANK_SPACING })
  for (const node of nodes) {
    graph.setNode(node.id, {
      width: node.measured?.width ?? HOSTED_AGENT_MAP_NODE_WIDTH,
      height: node.measured?.height ?? HOSTED_AGENT_MAP_NODE_HEIGHT,
    })
  }
  for (const edge of edges) {
    graph.setEdge(edge.source, edge.target)
  }
  dagre.layout(graph)

  for (const node of nodes) {
    const placed = graph.node(node.id)
    positions[node.id] = { x: placed.x - placed.width / 2, y: placed.y - placed.height / 2 }
  }
  return positions
}
